/**
 * A report's snapshots: its content kept at a moment.
 *
 * Every function returns the resource itself rather than the envelope it came
 * in, as the drafts' do. A list carries no content; `loadSnapshot` brings the
 * content of one.
 */

import { http, type RequestOptions } from '@/api/http'
import type { DraftFull } from '@/domain/draft'
import type { JsonObject } from '@/domain/json'
import { readSnapshot, type SnapshotSummary, type StoredSnapshot } from '@/domain/snapshot'

interface ListEnvelope {
  snapshots: StoredSnapshot[]
}

interface OneEnvelope {
  snapshot: StoredSnapshot
}

interface FullEnvelope {
  snapshot: StoredSnapshot & { payload: JsonObject }
}

interface DraftEnvelope {
  draft: DraftFull
}

interface DeletedEnvelope {
  deleted: boolean
}

const base = (draftId: number): string => `/reportDrafts/${draftId}/snapshots`

/** A report's snapshots, the newest first. */
export async function listSnapshots(
  draftId: number,
  options: RequestOptions = {},
): Promise<SnapshotSummary[]> {
  const { snapshots } = await http.get<ListEnvelope>(base(draftId), options)
  return snapshots.map(readSnapshot)
}

/** Keep the report as it is now. */
export async function createSnapshot(
  draftId: number,
  body: { label: string; comment: string },
  options: RequestOptions = {},
): Promise<SnapshotSummary> {
  const { snapshot } = await http.post<OneEnvelope>(base(draftId), body, options)
  return readSnapshot(snapshot)
}

/** One snapshot with the content it kept. */
export async function loadSnapshot(
  draftId: number,
  id: number,
  options: RequestOptions = {},
): Promise<{ summary: SnapshotSummary; payload: JsonObject }> {
  const { snapshot } = await http.get<FullEnvelope>(`${base(draftId)}/${id}`, options)
  return { summary: readSnapshot(snapshot), payload: snapshot.payload }
}

/** Rename a snapshot or change its comment. Refused once the report was validated. */
export async function updateSnapshot(
  draftId: number,
  id: number,
  body: { label?: string; comment?: string },
  options: RequestOptions = {},
): Promise<SnapshotSummary> {
  const { snapshot } = await http.put<OneEnvelope>(`${base(draftId)}/${id}`, body, options)
  return readSnapshot(snapshot)
}

export async function deleteSnapshot(
  draftId: number,
  id: number,
  options: RequestOptions = {},
): Promise<boolean> {
  const { deleted } = await http.delete<DeletedEnvelope>(`${base(draftId)}/${id}`, options)
  return deleted
}

/** Put a snapshot's content back as the report's. What it replaces is kept first. */
export async function restoreSnapshot(
  draftId: number,
  id: number,
  options: RequestOptions = {},
): Promise<DraftFull> {
  const { draft } = await http.post<DraftEnvelope>(`${base(draftId)}/${id}/restore`, {}, options)
  return draft
}
